/* eslint-disable no-nested-ternary */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Skeleton } from "@mui/material";
import { RootStateOrAny, useSelector } from "react-redux";

import ModalFulscreen from "components/ModalFullscreen";
import { CARGO_CODES } from "constants/permissions";
import { Context } from "context/Context";
import { AuthState } from "store/ducks/auth/types";
import { useGetEtapas } from "services/api/Etapas_Subetapas/etapas-get";
import { OrigemRequisicao } from "services/api/Etapas_Subetapas/subetapas-get";
import {
  IndicadoresCorretor,
  TipoVisualizacaoIndicadores,
  useIndicadoresConsolidados,
} from "services/api/Common_Leads/indicadores-consolidados";
import {
  PerfilHierarquico,
  useUsuariosPorPerfilHierarquico,
} from "services/api/Identity_Usuario/usuarios-por-perfil-hierarquico";
import axiosLead from "services/lead";
import { TipoAgendamento } from "services/api/Common_Leads/indicadores-agendamento";
import * as IconsSVG from "assets/iconsSVG";
import CyrelaLogo from "assets/logo/cyrela_logo.jpg";
import { getTotalItensRetorno } from "screens/leads/components/tableGrid/constants";
import * as HomeStyles from "../../styles";
import * as S from "./performanceModal.styles";
import AgendamentoDetails from "./AgendamentoDetails";

interface HierarchyOption {
  value: string | null;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initialTab?: number;
}

// Índices das abas de agendamento na barra: Atualização(0) | Visita(1) |
// Assinatura(2) | Temperatura(3) | Etapa(4).
const TAB_VISITA = 1;
const TAB_ASSINATURA = 2;

interface MetricConfig {
  key: string;
  fallbackKeys?: string[];
  label: string;
  className?: string;
  icon?: JSX.Element;
  statusFilter?: number[];
  etapaNome?: string;
}

const metricsByTab: Record<TipoVisualizacaoIndicadores, MetricConfig[]> = {
  [TipoVisualizacaoIndicadores.Atualizacao]: [
    {
      key: "atualizados",
      fallbackKeys: ["atualizado"],
      label: "Atualizados",
      className: "success",
      icon: <IconsSVG.SuccessIconGreen />,
      statusFilter: [1],
    },
    {
      key: "desatualizara",
      label: "Desatualizará em breve",
      className: "warning",
      icon: <IconsSVG.AlertIconOrange />,
      statusFilter: [2],
    },
    {
      key: "desatualizados",
      fallbackKeys: ["desatualizado"],
      label: "Desatualizados",
      className: "danger",
      icon: <IconsSVG.ClockIconRed />,
      statusFilter: [3],
    },
  ],
  [TipoVisualizacaoIndicadores.Temperatura]: [
    {
      key: "frio",
      label: "Frio",
      className: "info",
      icon: <IconsSVG.FrioIconBlue />,
    },
    {
      key: "morno",
      label: "Morno",
      className: "warning",
      icon: <IconsSVG.MornoIconYellow />,
    },
    {
      key: "quente",
      label: "Quente",
      className: "danger",
      icon: <IconsSVG.QuenteIconRed />,
    },
  ],
  [TipoVisualizacaoIndicadores.Etapa]: [
    {
      key: "novoLead",
      label: "Novo lead",
      icon: <IconsSVG.IndicatorUserIcon />,
      etapaNome: "Novo Lead",
    },
    {
      key: "contato",
      fallbackKeys: ["emContato"],
      label: "Em Contato",
      icon: <IconsSVG.IndicatorPhoneIcon />,
      etapaNome: "Em Contato",
    },
    {
      key: "nutricao",
      fallbackKeys: ["emNutricao"],
      label: "Em Nutrição",
      icon: <IconsSVG.UpdatedOutlined width={26} height={20} />,
      etapaNome: "Em Nutrição",
    },
    {
      key: "visita",
      label: "Visita",
      icon: <IconsSVG.IndicatorStoreIcon />,
      etapaNome: "Visita",
    },
    {
      key: "negociacao",
      label: "Em negociação",
      icon: <IconsSVG.EmNegociacaoIcon width={16} height={18} />,
      etapaNome: "Em Negociação",
    },
    {
      key: "comprou",
      label: "Comprou",
      icon: <IconsSVG.VendidoIcon width={24} height={18} />,
      etapaNome: "Comprou",
    },
    {
      key: "bolsao",
      label: "Bolsão",
      icon: <IconsSVG.BolsaoIcon />,
      etapaNome: "Bolsão",
    },
  ],
};

// Mapeia índice da aba → tipo de visualização de corretores. As abas de
// agendamento (Visita/Assinatura) não têm equivalente aqui (null).
const tabByIndex: (TipoVisualizacaoIndicadores | null)[] = [
  TipoVisualizacaoIndicadores.Atualizacao,
  null,
  null,
  TipoVisualizacaoIndicadores.Temperatura,
  TipoVisualizacaoIndicadores.Etapa,
];

const getMetricValue = (
  values: Record<string, number> | undefined,
  metric: MetricConfig
) => {
  if (!values) return 0;

  const foundKey = [metric.key, ...(metric.fallbackKeys || [])].find(
    (key) => values[key] !== undefined && values[key] !== null
  );

  return foundKey ? values[foundKey] : 0;
};

const getBrokerName = (corretor: IndicadoresCorretor) =>
  corretor.apelido || corretor.nome || "Corretor";

const getBrokerLeads = (corretor: IndicadoresCorretor) =>
  corretor.qtdLeads ?? corretor.quantidadeLeads ?? corretor.totalLeads ?? 0;

const getBrokerFilterId = (corretor: IndicadoresCorretor) =>
  corretor.idCorretor || "";

const PerformanceModal: React.FC<Props> = ({
  open,
  onClose,
  initialTab = 0,
}) => {
  const { filters, setFilters } = useContext(Context);
  const { codigoCargo2013, usuarioDiretor, usuarioGerenteOnline } = useSelector<
    RootStateOrAny,
    AuthState
  >((state) => state.user);
  const [tabValue, setTabValue] = useState(initialTab);

  // Ao abrir, respeita a aba solicitada por quem abriu (ex.: card da home).
  useEffect(() => {
    if (open) setTabValue(initialTab);
  }, [open, initialTab]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [temperaturas, setTemperaturas] = useState<any[]>([]);
  const [superintendenteSelecionado, setSuperintendenteSelecionado] =
    useState<HierarchyOption | null>(null);
  const [equipeSelecionada, setEquipeSelecionada] =
    useState<HierarchyOption | null>(null);
  const [corretorSelecionado, setCorretorSelecionado] =
    useState<HierarchyOption | null>(null);

  const isDiretor = usuarioDiretor === true;
  const showDiretorFilters = isDiretor || usuarioGerenteOnline;
  const isSuperintendente =
    codigoCargo2013 === CARGO_CODES.SUPERINTENDENTE && !isDiretor;
  const showHierarchy = showDiretorFilters || isSuperintendente;

  const { data: dataSuperintendentes } = useUsuariosPorPerfilHierarquico({
    queryKey: "performance-modal-superintendentes",
    perfil: PerfilHierarquico.Superintendente,
    enabled: open && showDiretorFilters,
  });

  const { data: dataEquipes } = useUsuariosPorPerfilHierarquico({
    queryKey: `performance-modal-equipes-${
      showDiretorFilters ? superintendenteSelecionado?.value || "self" : "self"
    }`,
    perfil: PerfilHierarquico.Gerente,
    idLider: showDiretorFilters
      ? superintendenteSelecionado?.value || null
      : null,
    enabled:
      open &&
      ((showDiretorFilters && Boolean(superintendenteSelecionado)) ||
        isSuperintendente),
  });

  const { data: dataCorretores } = useUsuariosPorPerfilHierarquico({
    queryKey: `performance-modal-corretores-${
      equipeSelecionada?.value || "all"
    }`,
    perfil: PerfilHierarquico.Corretor,
    idLider: equipeSelecionada?.value || null,
    enabled: open && showHierarchy && Boolean(equipeSelecionada),
  });

  const superintendentesOptions: HierarchyOption[] = useMemo(
    () =>
      dataSuperintendentes?.map((item) => ({
        value: item.idUsuario || item.idLider,
        label: item.nome,
      })) || [],
    [dataSuperintendentes]
  );

  const equipesOptions: HierarchyOption[] = useMemo(
    () =>
      dataEquipes?.map((item) => ({
        value: item.idUsuario || item.idLider,
        label: item.nome,
      })) || [],
    [dataEquipes]
  );

  const corretoresOptions: HierarchyOption[] = useMemo(
    () =>
      dataCorretores?.map((item) => ({
        value: item.id,
        label: item.nome,
      })) || [],
    [dataCorretores]
  );

  const idSuperintendenteFiltro =
    showDiretorFilters &&
    superintendenteSelecionado &&
    !equipeSelecionada &&
    !corretorSelecionado
      ? superintendenteSelecionado.value
      : undefined;
  const idGerenteFiltro =
    equipeSelecionada && !corretorSelecionado
      ? equipeSelecionada.value
      : undefined;
  const idCorretorFiltro = corretorSelecionado?.value || undefined;

  useEffect(() => {
    if (!open) {
      setSuperintendenteSelecionado(null);
      setEquipeSelecionada(null);
      setCorretorSelecionado(null);
    }
  }, [open]);

  useEffect(() => {
    setEquipeSelecionada(null);
    setCorretorSelecionado(null);
  }, [superintendenteSelecionado?.value]);

  useEffect(() => {
    setCorretorSelecionado(null);
  }, [equipeSelecionada?.value]);

  const isAgendamento = tabValue === TAB_VISITA || tabValue === TAB_ASSINATURA;
  const agendamentoTipo =
    tabValue === TAB_ASSINATURA
      ? TipoAgendamento.Assinatura
      : TipoAgendamento.Visita;
  // Nas abas de agendamento não há visualização de corretores; usa Atualização
  // como fallback só para manter as derivações abaixo válidas (não são exibidas).
  const tipoVisualizacao =
    tabByIndex[tabValue] ?? TipoVisualizacaoIndicadores.Atualizacao;
  const metrics = metricsByTab[tipoVisualizacao];
  const isStageTab = tipoVisualizacao === TipoVisualizacaoIndicadores.Etapa;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [tabValue]);

  useEffect(() => {
    setPage(1);
  }, [idSuperintendenteFiltro, idGerenteFiltro, idCorretorFiltro]);

  useEffect(() => {
    const requestTemperature = async () => {
      try {
        const response = await axiosLead.GetTemperature();
        setTemperaturas(response.data.data);
      } catch (error) {
        console.log("Erro ao listar temperaturas");
      }
    };

    if (open) requestTemperature();
  }, [open]);

  const { data: dataEtapa, refetch: refetchEtapas } = useGetEtapas();

  useEffect(() => {
    if (open) refetchEtapas();
  }, [open, refetchEtapas]);

  const queryKey = useMemo(
    () =>
      [
        "indicadores-consolidados",
        tipoVisualizacao,
        page,
        debouncedSearch,
        open,
        idSuperintendenteFiltro || "",
        idGerenteFiltro || "",
        idCorretorFiltro || "",
      ].join("-"),
    [
      debouncedSearch,
      open,
      page,
      tipoVisualizacao,
      idSuperintendenteFiltro,
      idGerenteFiltro,
      idCorretorFiltro,
    ]
  );

  const { data, isFetching } = useIndicadoresConsolidados({
    queryKey,
    enabled: open && !isAgendamento,
    TipoVisualizacao: tipoVisualizacao,
    PaginaAtual: page,
    TotalItensRetorno: 10,
    PalavraChave: debouncedSearch,
    IdUsuarioSuperintendente: idSuperintendenteFiltro
      ? [idSuperintendenteFiltro]
      : undefined,
    IdUsuarioGerente: idGerenteFiltro ? [idGerenteFiltro] : undefined,
    IdCorretor: idCorretorFiltro ? [idCorretorFiltro] : undefined,
  });
  const isInitialLoading = isFetching && !data;
  const hasNextPage = (data?.paginaAtual ?? 1) < (data?.totalPaginas ?? 1);

  const corretores = useMemo(() => {
    const occurrences = new Map<string, number>();

    return (data?.corretores || []).map((corretor) => {
      const baseKey = corretor.idCorretor365 || getBrokerName(corretor);
      const occurrence = occurrences.get(baseKey) ?? 0;

      occurrences.set(baseKey, occurrence + 1);

      return {
        ...corretor,
        cardKey: `${baseKey}-${occurrence}`,
      };
    });
  }, [data?.corretores]);

  const handleChangeTab = (_event: any, newValue: number) => {
    setTabValue(newValue);
  };

  const buildFilterState = (specificFilter: Record<string, any>) => ({
    OrdenarPorRecentes: null,
    InicioIntervaloCadastroLead: null,
    FimIntervaloCadastroLead: null,
    InicioIntervaloTransferencia: null,
    FimIntervaloTransferencia: null,
    FiltrarRecebidosPorTransferencia: null,
    OrigemRequisicao: OrigemRequisicao.Indicadores,
    ...specificFilter,
    paginationParams: {
      PaginaAtual: 1,
      PalavrasChave: "",
      TotalItensRetorno: getTotalItensRetorno(filters),
      OrdenarPorOrdemAlfabetica: "",
    },
  });

  const findValueEtapaByNome = (nomeEtapa: string) =>
    dataEtapa?.find((etapa: any) => etapa?.nome === nomeEtapa);

  const handleFilter = (
    metric: MetricConfig,
    corretor?: IndicadoresCorretor
  ) => {
    let indicatorFilter: Record<string, any> | null = null;

    if (tipoVisualizacao === TipoVisualizacaoIndicadores.Atualizacao) {
      indicatorFilter = { Status: metric.statusFilter };
    }

    if (tipoVisualizacao === TipoVisualizacaoIndicadores.Temperatura) {
      const found = temperaturas?.find(
        (item: any) => item?.nome === metric.label
      );
      indicatorFilter = {
        IdsTemperaturasLead: found ? [found.id] : [""],
      };
    }

    if (tipoVisualizacao === TipoVisualizacaoIndicadores.Etapa) {
      indicatorFilter = {
        Etapas: metric.etapaNome
          ? [findValueEtapaByNome(metric.etapaNome)?.id]
          : undefined,
      };
    }

    if (!indicatorFilter) return;

    const corretorId = corretor ? getBrokerFilterId(corretor) : "";
    const corretorFilter = corretorId ? { IdsCorretores: [corretorId] } : {};
    const newFilters = buildFilterState({
      ...indicatorFilter,
      ...corretorFilter,
    });

    setFilters(newFilters);
    localStorage.setItem("@sell.it/leadFilters", JSON.stringify(newFilters));
    window.location.href = "/leads?fromIndicator=true";
  };

  return (
    <ModalFulscreen
      open={open}
      setOpenModal={() => onClose()}
      onClose={onClose}
    >
      <S.Container>
        <S.Header>
          <S.HeaderTitle>
            <S.IconBox>
              <IconsSVG.ShortcutUserIcon />
            </S.IconBox>
            <S.Title>
              {tabValue === TAB_VISITA
                ? "Indicadores de Visita"
                : tabValue === TAB_ASSINATURA
                ? "Indicadores de Agendamento de Assinatura"
                : "Desempenho por Corretor"}
            </S.Title>
            {usuarioGerenteOnline && (
              <HomeStyles.GerenteOnlineBadge>
                <HomeStyles.GerenteOnlineBadgeIcon
                  src={CyrelaLogo}
                  alt="Logo Cyrela"
                />
                Dados referentes à mídia Online Cyrela
              </HomeStyles.GerenteOnlineBadge>
            )}
          </S.HeaderTitle>
          <S.CloseButton onClick={onClose} aria-label="Fechar">
            <IconsSVG.CloseIcon width={20} height={20} />
          </S.CloseButton>
        </S.Header>

        <HomeStyles.Box style={{ flex: "0 0 auto" }}>
          <HomeStyles.Tabs
            indicatorColor="secondary"
            value={tabValue}
            onChange={handleChangeTab}
          >
            <HomeStyles.Tab label="Atualização" />
            <HomeStyles.Tab label="Visita" />
            <HomeStyles.Tab label="Agendamento Assinatura" />
            <HomeStyles.Tab label="Temperatura" />
            <HomeStyles.Tab label="Etapa" />
          </HomeStyles.Tabs>
        </HomeStyles.Box>

        {isAgendamento ? (
          <AgendamentoDetails tipo={agendamentoTipo} />
        ) : (
          <S.Content>
            <S.FixedSection>
              <S.SummaryGrid className={isStageTab ? "stages" : ""}>
                {metrics.map((metric) => (
                  <S.SummaryCard
                    key={metric.key}
                    className={metric.className}
                    onClick={() => handleFilter(metric)}
                  >
                    {metric.icon}
                    <S.SummaryNumber>
                      {isFetching && !data
                        ? "--"
                        : getMetricValue(data?.consolidados, metric)}
                    </S.SummaryNumber>
                    <S.SummaryLabel>{metric.label}</S.SummaryLabel>
                  </S.SummaryCard>
                ))}
              </S.SummaryGrid>

              {showHierarchy && (
                <S.HierarchyRow className={showDiretorFilters ? "three" : ""}>
                  {showDiretorFilters && (
                    <S.Autocomplete
                      fullWidth
                      id="performance-modal-superintendente"
                      size="small"
                      options={superintendentesOptions}
                      getOptionLabel={(option: any) => option.label || ""}
                      // @ts-ignore
                      getOptionSelected={(option: any, value: any) =>
                        option.value === value.value
                      }
                      value={superintendenteSelecionado}
                      onChange={(_event: any, value: any) =>
                        setSuperintendenteSelecionado(
                          (value as HierarchyOption) || null
                        )
                      }
                      renderInput={(params) => (
                        <S.TextField
                          {...params}
                          placeholder="Selecionar Superintendente"
                          variant="outlined"
                        />
                      )}
                    />
                  )}
                  <S.Autocomplete
                    fullWidth
                    id="performance-modal-equipe"
                    size="small"
                    options={equipesOptions}
                    disabled={showDiretorFilters && !superintendenteSelecionado}
                    getOptionLabel={(option: any) => option.label || ""}
                    // @ts-ignore
                    getOptionSelected={(option: any, value: any) =>
                      option.value === value.value
                    }
                    value={equipeSelecionada}
                    onChange={(_event: any, value: any) =>
                      setEquipeSelecionada((value as HierarchyOption) || null)
                    }
                    renderInput={(params) => (
                      <S.TextField
                        {...params}
                        placeholder="Selecionar Equipe"
                        variant="outlined"
                      />
                    )}
                  />
                  <S.Autocomplete
                    fullWidth
                    id="performance-modal-corretor"
                    size="small"
                    options={corretoresOptions}
                    disabled={!equipeSelecionada}
                    getOptionLabel={(option: any) => option.label || ""}
                    // @ts-ignore
                    getOptionSelected={(option: any, value: any) =>
                      option.value === value.value
                    }
                    value={corretorSelecionado}
                    onChange={(_event: any, value: any) =>
                      setCorretorSelecionado((value as HierarchyOption) || null)
                    }
                    renderInput={(params) => (
                      <S.TextField
                        {...params}
                        disabled={!equipeSelecionada}
                        placeholder="Selecionar Corretor"
                        variant="outlined"
                      />
                    )}
                  />
                </S.HierarchyRow>
              )}

              <S.SearchWrap>
                <IconsSVG.SearchIcon />
                <S.SearchInput
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Pesquisar corretor..."
                />
              </S.SearchWrap>

              <S.TableHeader className={isStageTab ? "stages" : ""}>
                {(isStageTab ? ["full"] : ["left", "right"]).map((group) => (
                  <S.HeaderGroup
                    key={group}
                    className={isStageTab ? "stages" : ""}
                  >
                    <S.HeaderCell>Corretor</S.HeaderCell>
                    {metrics.map((metric) => (
                      <S.HeaderCell
                        key={metric.key}
                        className={metric.className}
                      >
                        {metric.label}
                      </S.HeaderCell>
                    ))}
                  </S.HeaderGroup>
                ))}
              </S.TableHeader>
            </S.FixedSection>

            <S.ScrollArea>
              {isInitialLoading && (
                <S.BrokerList className={isStageTab ? "stages" : ""}>
                  {[0, 1, 2, 3, 4, 5].map((item) => (
                    <Skeleton
                      key={item}
                      variant="rounded"
                      width="100%"
                      height={72}
                    />
                  ))}
                </S.BrokerList>
              )}

              {!isInitialLoading && corretores.length > 0 && (
                <>
                  <S.BrokerList className={isStageTab ? "stages" : ""}>
                    {corretores.map((corretor) => (
                      <S.BrokerCard
                        key={corretor.cardKey}
                        className={isStageTab ? "stages" : ""}
                      >
                        <S.BrokerInfo>
                          <S.Avatar>
                            {corretor.urlFoto ? (
                              <img
                                src={corretor.urlFoto}
                                alt={getBrokerName(corretor)}
                              />
                            ) : (
                              <IconsSVG.UserIconMin />
                            )}
                          </S.Avatar>
                          <div>
                            <S.BrokerName>
                              {getBrokerName(corretor)}
                            </S.BrokerName>
                            <S.BrokerLeads>
                              {getBrokerLeads(corretor)} leads
                            </S.BrokerLeads>
                          </div>
                        </S.BrokerInfo>
                        {metrics.map((metric) => (
                          <S.Metric
                            key={metric.key}
                            className={metric.className}
                            onClick={() => handleFilter(metric, corretor)}
                          >
                            {getMetricValue(corretor.indicadores, metric)}
                          </S.Metric>
                        ))}
                      </S.BrokerCard>
                    ))}
                  </S.BrokerList>
                  {hasNextPage && (
                    <S.LoadMore
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Carregar mais
                    </S.LoadMore>
                  )}
                </>
              )}

              {!isInitialLoading && corretores.length === 0 && (
                <S.Empty>Nenhum corretor encontrado</S.Empty>
              )}
            </S.ScrollArea>
          </S.Content>
        )}
      </S.Container>
    </ModalFulscreen>
  );
};

export default PerformanceModal;
